import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Avatar from '@mui/material/Avatar';
import ExtensionIcon from '@mui/icons-material/Extension';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';

const defaultIcon = <ExtensionIcon />;

export default function IntegrationCard({ service, onSave, serviceImagesMap = {} }) {
    const [open, setOpen] = useState(false);
    const [formValues, setFormValues] = useState(
        service.parameters.map(p => ({ ...p }))
    );
    const [touched, setTouched] = useState({});
    const [showPasswords, setShowPasswords] = useState({});

    const isConnected = service.lastUpdated !== null;
    const isFormValid = formValues.every(p => !p.required || (p.value && p.value.trim() !== ''));

    const [saving, setSaving] = useState(false);

    const handleToggleVisibility = (index) => {
        setShowPasswords(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleOpen = () => {
        setFormValues(service.parameters.map(p => ({ ...p })));
        setTouched({});
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (index, value) => {
        setFormValues(prev => prev.map((p, i) =>
            i === index ? { ...p, value } : p
        ));
    };

    const handleSave = async () => {
        if (onSave) {
            setSaving(true);
            try {
                await onSave(service.serviceName, formValues);
            } finally {
                setSaving(false);
                setOpen(false);
            }
        }
    };

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    minHeight: 230,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <CardContent>
                    <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', pt: 1 }}>
                        <Box sx={{ color: 'primary.main', display: 'flex' }}>
                            <Avatar sx={{ width: 70, height: 70 }} src={serviceImagesMap[service.serviceName]} >
                                {!serviceImagesMap[service.serviceName] && defaultIcon}
                            </Avatar>
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" fontSize={20}>
                            {service.serviceName}
                        </Typography>
                        {isConnected ? (
                            <Stack spacing={1} alignItems="center">
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Connected"
                                    color="success"
                                    size="small"
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpen}
                                >
                                    Edit
                                </Button>
                            </Stack>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpen}
                                sx={{
                                    color: '#969696',
                                    borderColor: '#c7c7c7',
                                    '&:hover': {
                                        borderColor: '#bdbdbd',
                                        backgroundColor: '#fafafa',
                                    },
                                }}
                            >
                                Connect
                            </Button>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ width: 70, height: 70 }} src={serviceImagesMap[service.serviceName]} >
                        {!serviceImagesMap[service.serviceName] && defaultIcon}
                    </Avatar>
                    {isConnected ? `Edit ${service.serviceName}` : `Connect ${service.serviceName}`}
                    {service.lastUpdated && (
                        <Typography variant="caption" display="block" color="text.secondary">
                            Last updated: {new Date(service.lastUpdated).toLocaleString()} UTC
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {formValues.map((param, index) => (
                            <TextField
                                key={param.label}
                                label={param.label}
                                value={param.value || ''}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onBlur={() => setTouched(prev => ({ ...prev, [index]: true }))}
                                fullWidth
                                size="small"
                                required={param.required}
                                error={param.required && touched[index] && (!param.value || param.value.trim() === '')}
                                helperText={param.required && touched[index] && (!param.value || param.value.trim() === '') ? 'Required field' :
                                    ''}
                                type={showPasswords[index] ? 'text' : 'password'}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => handleToggleVisibility(index)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPasswords[index] ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!isFormValid || saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}